"use client";

import { useState, useEffect, useCallback } from "react";
import { LoginPage } from "@/components/LoginPage";
import { Header } from "@/components/Header";
import { ContractsTable } from "@/components/ContractsTable";
import { ResultsView } from "@/components/ResultsView";
import {
  Portal,
  Contract,
  CalculationResult,
  getPortals,
  connectPortal,
  expandContract,
  calculateContractsWithProgress,
  contractKey,
  logout,
  getStoredUser,
} from "@/lib/api";

export default function Dashboard() {
  const [user, setUser] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [portals, setPortals] = useState<Portal[]>([]);
  const [activePortal, setActivePortal] = useState<string | null>(null);
  const [contracts, setContracts] = useState<Contract[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [results, setResults] = useState<CalculationResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [calcLoading, setCalcLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [loadingStatus, setLoadingStatus] = useState("");
  const [calcStatus, setCalcStatus] = useState("");
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);

  useEffect(() => {
    const stored = getStoredUser();
    if (stored) setUser(stored);
    setAuthChecked(true);
  }, []);

  useEffect(() => {
    if (!user) return;
    getPortals()
      .then(setPortals)
      .catch(() => setError("Nie udalo sie zaladowac portali"));
  }, [user]);

  const handleLogin = useCallback((username: string) => {
    setUser(username);
  }, []);

  const handleLogout = useCallback(() => {
    logout();
    setUser(null);
    setPortals([]);
    setActivePortal(null);
    setContracts([]);
    setSelected(new Set());
    setResults([]);
  }, []);

  const handlePortalClick = useCallback(async (name: string) => {
    setLoading(true);
    setError(null);
    setResults([]);
    setLoadingStatus("Laczenie z portalem...");
    try {
      const data = await connectPortal(name);
      setActivePortal(name);

      // Expand multi-vehicle contracts one by one
      const allContracts: Contract[] = [];
      const multiVehicle = data.contracts.filter((c) => !c.registration_number);
      const singleVehicle = data.contracts.filter((c) => c.registration_number);
      allContracts.push(...singleVehicle);

      for (const contract of multiVehicle) {
        setLoadingStatus(`Pobieram dane dla ${contract.contract_number}`);
        try {
          const expanded = await expandContract(name, contract.contract_number);
          allContracts.push(...expanded.contracts);
        } catch {
          allContracts.push(contract);
        }
      }

      setContracts(allContracts);
      setSelected(new Set(allContracts.map((c) => contractKey(c))));
    } catch (e) {
      setError(
        `Blad polaczenia z portalem ${name}: ${e instanceof Error ? e.message : "Nieznany blad"}`
      );
    } finally {
      setLoading(false);
      setLoadingStatus("");
    }
  }, []);

  const handleToggle = useCallback((contractNumber: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(contractNumber)) {
        next.delete(contractNumber);
      } else {
        next.add(contractNumber);
      }
      return next;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelected(new Set(contracts.map((c) => contractKey(c))));
  }, [contracts]);

  const handleDeselectAll = useCallback(() => {
    setSelected(new Set());
  }, []);

  const handleCalculate = useCallback(async (calcYear: number, calcMonth: number) => {
    if (!activePortal || selected.size === 0) return;
    setCalcLoading(true);
    setCalcStatus("");
    setError(null);
    try {
      const allResults = await calculateContractsWithProgress(
        activePortal,
        Array.from(selected),
        calcYear,
        calcMonth,
        (regNumber) => setCalcStatus(`Obliczanie danych dla ${regNumber}`)
      );
      setResults(allResults);
    } catch (e) {
      setError(
        `Blad obliczen: ${e instanceof Error ? e.message : "Nieznany blad"}`
      );
    } finally {
      setCalcLoading(false);
      setCalcStatus("");
    }
  }, [activePortal, selected]);

  // Don't render anything until we check localStorage
  if (!authChecked) return null;

  // Show login page if not authenticated
  if (!user) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-[#0b1120]">
      {/* Background gradient effect */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-blue-600/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-0 right-0 w-[600px] h-[300px] bg-blue-800/5 rounded-full blur-[80px]" />
      </div>

      <div className="relative z-10">
        <Header
          portals={portals}
          activePortal={activePortal}
          onPortalClick={handlePortalClick}
          loading={loading}
          username={user}
          onLogout={handleLogout}
        />

        <main className="max-w-7xl mx-auto px-6 py-8 space-y-8">
          {/* Error message */}
          {error && (
            <div className="glass rounded-xl p-4 border border-red-500/30 bg-red-500/5">
              <p className="text-red-400 text-sm">{error}</p>
            </div>
          )}

          {/* Welcome state */}
          {!activePortal && !loading && (
            <div className="flex flex-col items-center justify-center py-32">
              <div className="w-24 h-24 rounded-3xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center glow-blue mb-8 animate-border-glow border-2 border-blue-500/30">
                <span className="text-white font-bold text-4xl">H</span>
              </div>
              <h2 className="text-3xl font-bold gradient-text mb-4">
                Hitmark Leasing Calculator
              </h2>
              <p className="text-muted-foreground text-center max-w-md">
                Wybierz portal leasingowy z gornego paska, aby pobrac liste umow
                i wykonac obliczenia.
              </p>
            </div>
          )}

          {/* Loading state */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-32">
              <div className="spinner !w-12 !h-12 mb-6" />
              <p className="text-blue-300">{loadingStatus || "Laczenie z portalem..."}</p>
            </div>
          )}

          {/* Contracts table */}
          {!loading && contracts.length > 0 && (
            <ContractsTable
              contracts={contracts}
              selected={selected}
              onToggle={handleToggle}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
              onCalculate={handleCalculate}
              loading={calcLoading}
              year={year}
              month={month}
              onYearChange={setYear}
              onMonthChange={setMonth}
            />
          )}

          {/* Calculation loading */}
          {calcLoading && (
            <div className="flex items-center justify-center py-12">
              <div className="spinner !w-8 !h-8 mr-4" />
              <p className="text-blue-300">{calcStatus || "Obliczanie danych..."}</p>
            </div>
          )}

          {/* Results */}
          {!calcLoading && results.length > 0 && (
            <ResultsView results={results} portalName={activePortal || ""} />
          )}
        </main>
      </div>
    </div>
  );
}
